import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  Users, Gift, DollarSign, Info, CalendarDays,
} from 'lucide-react';
import { TAX_RATE } from '../constants/bookingCatalog';
import {
  AddExtrasMenu, computeAddOnsTotal, computeCustomExtrasTotal,
  buildAddOnsPayload, buildCustomChargesPayload, buildDesignsPayload, seedFromBooking,
} from './AddExtrasMenu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const hoursUntil = (dateStr) => {
  if (!dateStr) return Infinity;
  try { return (new Date(dateStr).getTime() - Date.now()) / 3_600_000; } catch { return Infinity; }
};

/**
 * Customer-facing restricted edit dialog.
 * Allowed: add-ons, predefined extras, custom extras, guest count.
 * Disallowed: date/time/package/customer info (use Reschedule for date/time/package).
 */
export const CustomerEditBookingDialog = ({ booking, open, onClose, onSaved }) => {
  const [guestCount, setGuestCount] = useState(1);
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [extrasQty, setExtrasQty] = useState({});
  const [customExtras, setCustomExtras] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!booking || !open) return;
    setGuestCount(booking.guest_count || 1);
    const seeded = seedFromBooking(booking);
    setSelectedAddOns(seeded.selectedAddOns);
    setExtrasQty(seeded.extrasQty);
    setCustomExtras(seeded.customExtras);
    setDesigns(seeded.designs);
  }, [booking, open]);

  useEffect(() => {
    if (!open || catalog) return;
    axios.get(`${API_URL}/api/addons/catalog`)
      .then((r) => setCatalog(r.data || { categories: [] }))
      .catch(() => setCatalog({ categories: [] }));
  }, [open, catalog]);

  const withinCutoff = booking && hoursUntil(booking.date) < 48;
  const isCancelled = booking && booking.status === 'cancelled';

  const totals = useMemo(() => {
    if (!booking) return null;
    const pkgPrice = Number(booking.total || 0) - Number(booking.tax || 0) - Number(booking.add_ons_total || 0) - Number(booking.custom_charges_total || 0) + Number(booking.discount?.amount || 0);
    const effectivePkg = Math.max(0, Math.round(pkgPrice * 100) / 100);
    const addOnsTotal = computeAddOnsTotal(catalog, selectedAddOns, extrasQty);
    const customTotal = computeCustomExtrasTotal(customExtras);
    const preDiscount = effectivePkg + addOnsTotal + customTotal;
    const discountAmount = Number(booking.discount?.amount || 0);
    const afterDiscount = Math.max(0, preDiscount - discountAmount);
    const tax = Math.round(afterDiscount * TAX_RATE * 100) / 100;
    const total = Math.round((afterDiscount + tax) * 100) / 100;
    const paid = booking.deposit_paid ? Number(booking.deposit_amount || total * 0.5) : 0;
    const diffFromOriginal = Math.round((total - Number(booking.total || 0)) * 100) / 100;
    return {
      packagePrice: effectivePkg,
      addOnsTotal: Math.round(addOnsTotal * 100) / 100,
      customTotal: Math.round(customTotal * 100) / 100,
      discountAmount,
      tax,
      total,
      balance: Math.round(Math.max(total - paid, 0) * 100) / 100,
      diffFromOriginal,
    };
  }, [booking, catalog, selectedAddOns, extrasQty, customExtras]);

  if (!booking) return null;

  const save = async () => {
    if (withinCutoff) { toast.error("Bookings within 48 hours can't be edited."); return; }
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/bookings/${booking.booking_id}/customer-edit`, {
        guest_count: Number(guestCount) || 1,
        add_ons: buildAddOnsPayload(catalog, selectedAddOns, extrasQty),
        custom_charges: buildCustomChargesPayload(customExtras),
        designs: buildDesignsPayload(designs),
        reason: 'Customer self-edit',
      }, { withCredentials: true });
      toast.success('Booking updated');
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving && !o) onClose?.(); }}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="customer-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl" style={{ fontFamily: 'Fredoka' }}>
            <Gift className="w-6 h-6 text-[#FF3B6B]" /> Update Your Party Extras
          </DialogTitle>
          <DialogDescription>
            Add or change add-ons, custom extras, and guest count. To change the date, time or package, use <strong>Reschedule</strong>.
          </DialogDescription>
        </DialogHeader>

        {withinCutoff && !isCancelled && (
          <div className="rounded-xl border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800 flex items-start gap-2" data-testid="cutoff-warning">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            Your party is less than 48 hours away — changes are locked. Please call us for urgent edits.
          </div>
        )}
        {isCancelled && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            This booking is cancelled and cannot be edited.
          </div>
        )}

        {/* Locked party details (read-only) */}
        <section className="rounded-2xl border border-[#E8E5DF] bg-[#FFFDF8] p-4 text-sm grid grid-cols-2 gap-2">
          <div><Label className="text-xs text-[#6B6B7A] flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Date</Label><p className="text-[#1E1E24]">{booking.date}</p></div>
          <div><Label className="text-xs text-[#6B6B7A]">Time</Label><p className="text-[#1E1E24]">{booking.time_slot}</p></div>
          <div><Label className="text-xs text-[#6B6B7A]">Package</Label><p className="text-[#1E1E24]">{booking.package_name}</p></div>
          <div>
            <Label className="text-xs text-[#6B6B7A] flex items-center gap-1"><Users className="w-3 h-3" /> Guest count</Label>
            <Input type="number" min="1" max="50" value={guestCount}
              onChange={(e) => setGuestCount(e.target.value)}
              disabled={withinCutoff || isCancelled}
              className="mt-1 h-8" data-testid="ce-guest-count" />
          </div>
        </section>

        {/* Add Extras (optional) — unified menu */}
        <section className="rounded-2xl border border-[#E8E5DF] p-4 space-y-3">
          <h3 className="flex items-center gap-2 font-semibold text-[#1E1E24]"><Gift className="w-4 h-4" /> Add Extras <span className="text-sm font-normal text-[#6B6B7A]">(optional)</span></h3>
          <AddExtrasMenu
            catalog={catalog}
            selectedAddOns={selectedAddOns} setSelectedAddOns={setSelectedAddOns}
            extrasQty={extrasQty} setExtrasQty={setExtrasQty}
            customExtras={customExtras} setCustomExtras={setCustomExtras}
            designs={designs} setDesigns={setDesigns}
            childName={booking.child_name}
            disabled={withinCutoff || isCancelled}
          />
        </section>

        {/* Totals */}
        {totals && (
          <section className="rounded-2xl border border-[#E8E5DF] bg-[#FFF9F5] p-4 space-y-1 text-sm" data-testid="ce-totals">
            <Row label="Package" value={`$${totals.packagePrice.toFixed(2)}`} />
            <Row label="Add-ons" value={`$${totals.addOnsTotal.toFixed(2)}`} />
            <Row label="Custom extras" value={`$${totals.customTotal.toFixed(2)}`} />
            {totals.discountAmount > 0 && <Row label="Discount" value={`-$${totals.discountAmount.toFixed(2)}`} color="#06D6A0" />}
            <Row label="Tax (13%)" value={`$${totals.tax.toFixed(2)}`} muted />
            <div className="border-t border-[#E8E5DF] my-1" />
            <Row label={<span className="font-semibold">New Total</span>} value={<span className="font-bold">${totals.total.toFixed(2)}</span>} />
            <Row label="Balance owing" value={`$${totals.balance.toFixed(2)}`} muted />
            {Math.abs(totals.diffFromOriginal) > 0.009 && (
              <p className={`text-xs mt-1 ${totals.diffFromOriginal > 0 ? 'text-[#FF3B6B]' : 'text-[#06D6A0]'}`} data-testid="ce-diff">
                {totals.diffFromOriginal > 0 ? '+' : ''}${totals.diffFromOriginal.toFixed(2)} vs current total
              </p>
            )}
          </section>
        )}

        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-full" disabled={saving} data-testid="ce-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || withinCutoff || isCancelled}
            className="flex-1 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="ce-save">
            <DollarSign className="w-4 h-4 mr-1" />{saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value, muted = false, color }) => (
  <div className="flex items-center justify-between">
    <span className={muted ? 'text-[#6B6B7A]' : 'text-[#1E1E24]'} style={color ? { color } : undefined}>{label}</span>
    <span className={muted ? 'text-[#6B6B7A]' : 'text-[#1E1E24]'} style={color ? { color } : undefined}>{value}</span>
  </div>
);

export default CustomerEditBookingDialog;
